import { DataTable, Select } from "@design";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { MessageSquareText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { listAllFeedbackOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { Text } from "@/components/Text";
import { FEEDBACK_CATEGORIES } from "@/features/feedback/feedbackForm";
import { AdminPage } from "../AdminPage";
import { useFeedbackColumns } from "./feedbackColumns";
import { FEEDBACK_STATUSES } from "./feedbackLabels";

const PAGE_SIZE = 50;
const ALL = "all";

/** /admin/feedback: everything users submitted, newest first, filtered and paged on the server. */
export function FeedbackPage() {
  const { t } = useTranslation("adminFeedback");
  const navigate = useNavigate();
  const columns = useFeedbackColumns();
  const [category, setCategory] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [page, setPage] = useState(1);

  const feedback = useQuery({
    ...listAllFeedbackOptions({
      query: {
        page,
        page_size: PAGE_SIZE,
        category: category === ALL ? undefined : category,
        status: status === ALL ? undefined : status,
      },
    }),
    placeholderData: keepPreviousData,
  });
  const filtered = category !== ALL || status !== ALL;

  return (
    <AdminPage title={t("title")} subtitle={t("subtitle")}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="md:w-48">
          <Select
            aria-label={t("filter.category")}
            value={category}
            options={[
              { value: ALL, label: t("filter.allCategories") },
              ...FEEDBACK_CATEGORIES.map((c) => ({ value: c, label: t(`categories.${c}`) })),
            ]}
            onChange={(value) => {
              setCategory(value);
              setPage(1);
            }}
          />
        </div>
        <div className="md:w-48">
          <Select
            aria-label={t("filter.status")}
            value={status}
            options={[
              { value: ALL, label: t("filter.allStatuses") },
              ...FEEDBACK_STATUSES.map((s) => ({ value: s, label: t(`statuses.${s}`) })),
            ]}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
          />
        </div>
        {feedback.data && (
          <Text as="span" variant="body-sm" tone="tertiary" className="md:ml-auto">
            {t("total", { count: feedback.data.meta.total })}
          </Text>
        )}
      </div>
      {feedback.isError ? (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("tableLabel")}
          columns={columns}
          rows={feedback.data?.data ?? []}
          rowKey={(item) => item.uuid}
          loading={feedback.isPending}
          onRowClick={(item) => navigate(`/admin/feedback/${item.uuid}`)}
          empty={<EmptyState icon={<MessageSquareText size={32} />} title={filtered ? t("noResults") : t("empty")} />}
          pagination={{ page, pageCount: Math.max(1, feedback.data?.meta.totalPages ?? 1), onPageChange: setPage }}
        />
      )}
    </AdminPage>
  );
}
